import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
  type RefObject,
} from 'react';

interface HeightSyncContextValue {
  /** The tallest measured height (px) across every registered consumer. */
  maxHeight: number;
  /** Report a consumer's natural (unconstrained) height. */
  measure: (id: string, height: number) => void;
  /** Drop a consumer's contribution when it unmounts. */
  release: (id: string) => void;
}

export interface HeightSync {
  Provider: FC<PropsWithChildren>;
  useHeightSync: (enabled?: boolean) => {
    /** Ref for the (unconstrained) element whose natural height is measured. */
    measureRef: RefObject<HTMLDivElement | null>;
    /** Ref for the element that reserves the shared max height as a min-height. */
    reserveRef: RefObject<HTMLDivElement | null>;
  };
}

/**
 * Builds a self-contained "reserve the tallest sibling height" primitive. Each consumer reports the
 * natural height of one element (`measureRef`) and the provider tracks the tallest across all
 * consumers, which every consumer then reserves as a min-height on another element (`reserveRef`).
 * This lines up sibling offer cards without hard-coding a min-height that looks off when the
 * measured content only takes one line. Instantiate one per region to align (title, ribbon, …) so
 * their max heights never bleed into each other.
 */
export const createHeightSync = (): HeightSync => {
  const HeightSyncContext = createContext<HeightSyncContextValue | null>(null);

  const Provider: FC<PropsWithChildren> = ({ children }) => {
    const heightsRef = useRef<Map<string, number>>(new Map());
    const [maxHeight, setMaxHeight] = useState(0);

    const recompute = useCallback(() => {
      const heights = [...heightsRef.current.values()];
      setMaxHeight(heights.length ? Math.max(...heights) : 0);
    }, []);

    const measure = useCallback(
      (id: string, height: number) => {
        if (heightsRef.current.get(id) === height) return;
        heightsRef.current.set(id, height);
        recompute();
      },
      [recompute],
    );

    const release = useCallback(
      (id: string) => {
        if (heightsRef.current.delete(id)) recompute();
      },
      [recompute],
    );

    const value = useMemo(() => ({ maxHeight, measure, release }), [maxHeight, measure, release]);

    return <HeightSyncContext.Provider value={value}>{children}</HeightSyncContext.Provider>;
  };

  const useHeightSync = (enabled = true) => {
    const context = useContext(HeightSyncContext);
    const id = useId();
    const measureRef = useRef<HTMLDivElement>(null);
    const reserveRef = useRef<HTMLDivElement>(null);

    // `measure`/`release` are stable, so the observer is set up once per mount. `maxHeight` is
    // applied separately (below) so a new reservation never re-subscribes the observer.
    const measure = context?.measure;
    const release = context?.release;
    const maxHeight = context?.maxHeight ?? 0;

    useEffect(() => {
      const element = measureRef.current;
      if (!measure || !release || !enabled || !element) return;

      const observer = new ResizeObserver(entries => {
        measure(id, entries[0]?.contentRect.height ?? element.getBoundingClientRect().height);
      });
      observer.observe(element);

      return () => {
        observer.disconnect();
        release(id);
      };
    }, [measure, release, enabled, id]);

    // Reserve the shared tallest height on the reserve element so every sibling's next rows line up.
    useEffect(() => {
      const reserve = reserveRef.current;
      if (!reserve) return;
      reserve.style.minHeight = enabled && maxHeight ? `${maxHeight}px` : '';
    }, [enabled, maxHeight]);

    return { measureRef, reserveRef };
  };

  return { Provider, useHeightSync };
};
